/**
 * Wall azimuth and window orientation helpers.
 * Azimuth logic aligned with SHGCViewer calculateWallAzimuth.
 */

type Vertex = {
  vertex_x_coordinate?: number;
  vertex_y_coordinate?: number;
  vertex_z_coordinate?: number;
  x?: number;
  y?: number;
  z?: number;
};

export const calculateWallAzimuth = (vertices: Vertex[]): number | null => {
  if (!vertices || vertices.length < 3) return null;

  const v0 = {
    x: vertices[0].vertex_x_coordinate ?? vertices[0].x ?? 0,
    y: vertices[0].vertex_y_coordinate ?? vertices[0].y ?? 0,
    z: vertices[0].vertex_z_coordinate ?? vertices[0].z ?? 0,
  };
  const v1 = {
    x: vertices[1].vertex_x_coordinate ?? vertices[1].x ?? 0,
    y: vertices[1].vertex_y_coordinate ?? vertices[1].y ?? 0,
    z: vertices[1].vertex_z_coordinate ?? vertices[1].z ?? 0,
  };
  const v2 = {
    x: vertices[2].vertex_x_coordinate ?? vertices[2].x ?? 0,
    y: vertices[2].vertex_y_coordinate ?? vertices[2].y ?? 0,
    z: vertices[2].vertex_z_coordinate ?? vertices[2].z ?? 0,
  };

  const vector1 = { x: v1.x - v0.x, y: v1.y - v0.y, z: v1.z - v0.z };
  const vector2 = { x: v2.x - v0.x, y: v2.y - v0.y, z: v2.z - v0.z };

  const normal = {
    x: vector1.y * vector2.z - vector1.z * vector2.y,
    y: vector1.z * vector2.x - vector1.x * vector2.z,
    z: vector1.x * vector2.y - vector1.y * vector2.x,
  };

  let azimuth = Math.atan2(normal.y, normal.x) * (180 / Math.PI);
  azimuth = (90 - azimuth) % 360;
  if (azimuth < 0) azimuth += 360;

  return azimuth;
};

export const getCardinalDirectionFromAzimuth = (azimuth: number): string => {
  let normalized = azimuth % 360;
  if (normalized < 0) normalized += 360;

  if (normalized >= 315 || normalized < 45) return 'North';
  if (normalized >= 45 && normalized < 135) return 'East';
  if (normalized >= 135 && normalized < 225) return 'South';
  if (normalized >= 225 && normalized < 315) return 'West';
  return 'Unknown';
};

export const get8DirectionFromAzimuth = (azimuth: number): string => {
  let normalizedAzimuth = azimuth % 360;
  if (normalizedAzimuth < 0) normalizedAzimuth += 360;

  const directions = [
    { min: 337.5, max: 360, name: 'North' },
    { min: 0, max: 22.5, name: 'North' },
    { min: 22.5, max: 67.5, name: 'North-East' },
    { min: 67.5, max: 112.5, name: 'East' },
    { min: 112.5, max: 157.5, name: 'South-East' },
    { min: 157.5, max: 202.5, name: 'South' },
    { min: 202.5, max: 247.5, name: 'South-West' },
    { min: 247.5, max: 292.5, name: 'West' },
    { min: 292.5, max: 337.5, name: 'North-West' },
  ];

  for (const dir of directions) {
    if (normalizedAzimuth >= dir.min && normalizedAzimuth <= dir.max) {
      return dir.name;
    }
  }

  return 'Unknown';
};

const getOrientationFromWindowName = (windowName: string): string => {
  const name = windowName.toLowerCase();
  if (name.includes('north') || name.includes('n_')) return 'North';
  if (name.includes('south') || name.includes('s_')) return 'South';
  if (name.includes('east') || name.includes('e_')) return 'East';
  if (name.includes('west') || name.includes('w_')) return 'West';
  return 'Unknown';
};

/** Fenestration rows counted as windows (excludes Door / subsurface doors). */
export const isWindowFenestration = (surface: any): boolean => {
  if (!surface) return false;
  if (surface.surface_type) {
    return surface.surface_type === 'Window';
  }
  return true;
};

export const getWindowOrientation = (
  windowName: string,
  window: any,
  wall: any | null
): string => {
  if (wall) {
    if (wall.vertices && wall.vertices.length >= 3) {
      const azimuth = calculateWallAzimuth(wall.vertices);
      if (azimuth != null) return getCardinalDirectionFromAzimuth(azimuth);
    }
    if (wall.azimuth != null && wall.azimuth !== undefined) {
      return getCardinalDirectionFromAzimuth(wall.azimuth);
    }
  }

  if (window?.orientation) {
    return window.orientation;
  }

  return getOrientationFromWindowName(windowName);
};

export const resolveBuildingWall = (
  jsonData: any,
  buildingSurfaceData: Record<string, any>,
  wallName: string | undefined
): any | null => {
  if (!wallName) return null;
  return (
    buildingSurfaceData[wallName] ||
    jsonData[wallName] ||
    jsonData.BuildingSurface?.[wallName] ||
    jsonData.Wall?.[wallName] ||
    null
  );
};
